import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';

import flyer0 from '../assets/flyer.png';
import flyer1 from '../assets/flyer1.png';
import flyer2 from '../assets/flyer2.png';
import flyer3 from '../assets/flyer3.png';

const slides = [flyer0, flyer1, flyer2, flyer3];

const stats = [
  { num: 400, suffix: '+', label: 'Students Enrolled' },
  { num: 30, suffix: '+', label: 'Expert Counsellors' },
  { num: 25, suffix: '+', label: 'University Partners' },
  { num: 98, suffix: '%', label: 'Satisfaction Rate' },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCurrent(p => (p + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" style={{
      background: '#0b1f4a',
      display: 'flex',
      flexDirection: 'column',
      marginTop: '74px', /* exact navbar height */
    }}>

      {/* ── TEXT CONTENT — TOP ── */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center',
        padding: '40px 5% 36px',
        background: '#0b1f4a',
      }}>

        {/* Badge */}
        <motion.div {...fade(0)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(245,197,24,0.35)',
          padding: '5px 14px', borderRadius: 999,
          color: '#f5c518', fontSize: 18, fontWeight: 600,
          marginBottom: 12, lineHeight: 1.4,
        }}>
          ★&nbsp; India's One of the Best Online Education Registration Platform
        </motion.div>

        {/* H1 */}
        <motion.h1 {...fade(0.1)} style={{
          fontSize: 'clamp(30px, 4vw, 52px)', lineHeight: 1.06,
          color: '#fff', fontWeight: 900, marginBottom: 10,
        }}>
          Learn Anything,<br />
          <em style={{ fontStyle: 'italic', color: '#f5c518' }}>On Your Schedule.</em>
        </motion.h1>

        {/* Subtext */}
        <motion.p {...fade(0.2)} style={{
          fontSize: 'clamp(12px, 1.2vw, 14px)', color: 'rgba(255,255,255,0.65)',
          maxWidth: 480, margin: '0 0 20px', lineHeight: 1.7,
        }}>
          iGrade Education connects ambitious students and working professionals to India's finest
          UGC-approved online degree programs — with expert guidance every step of the way.
        </motion.p>

        {/* Buttons */}
        <motion.div {...fade(0.3)} style={{
          display: 'flex', gap: 10, flexWrap: 'wrap',
          justifyContent: 'center', marginBottom: 28,
        }}>
          <a href="#contact" style={{
            background: '#f5c518', color: '#0b1f4a',
            padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all 0.25s',
            whiteSpace: 'nowrap', textDecoration: 'none',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(245,197,24,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >Get Free Counselling →</a>

          <a href="#universities" style={{
            background: 'transparent', color: '#fff',
            padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 13,
            border: '1.5px solid rgba(255,255,255,0.4)', transition: 'all 0.25s',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            whiteSpace: 'nowrap', textDecoration: 'none',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >Explore Universities</a>
        </motion.div>

        {/* Stats */}
        <motion.div {...fade(0.45)} ref={ref} style={{
          display: 'flex', gap: 0, flexWrap: 'wrap', justifyContent: 'center',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 20, width: '100%',
        }}>
          {stats.map((s, idx) => (
            <div key={s.label} style={{
              padding: '0 24px', textAlign: 'center',
              borderRight: idx < stats.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
            }}>
              <div style={{
                fontFamily: 'Playfair Display,serif',
                fontSize: 'clamp(18px, 2vw, 26px)',
                fontWeight: 900, color: '#fff', lineHeight: 1,
              }}>
                {inView ? <CountUp end={s.num} duration={2} separator="," /> : '0'}{s.suffix}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
      {/* ── END TEXT CONTENT ── */}

      {/* ── FLYER SLIDESHOW — BOTTOM ── */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '70vh',
        overflow: 'hidden',
        background: '#eeebe3',
      }}>
        {slides.map((src, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${src})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            opacity: i === current ? 1 : 0,
            transition: 'opacity 1.4s ease',
          }} />
        ))}

        {/* Dots */}
        <div style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: 8, zIndex: 5,
        }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => setCurrent(i)} style={{
              height: 5, width: i === current ? 20 : 5, borderRadius: 3, cursor: 'pointer',
              background: i === current ? '#f5c518' : 'rgba(11,31,74,0.35)',
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(11,31,74,0.08)', zIndex: 5 }}>
          <div key={current} style={{
            height: '100%', background: '#f5c518',
            animation: 'heroProgress 5s linear forwards',
          }} />
        </div>
      </div>
      {/* ── END SLIDESHOW ── */}

      <style>{`
        @keyframes heroProgress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @media (max-width: 600px) {
          #home { margin-top: 62px !important; }
          #home > div:last-child { height: 40vh !important; }
        }
      `}</style>
    </section>
  );
}